import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useCallback, } from "react";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
/**
 * Initial auth state before session check
 */
const initialAuthState = {
    isAuthenticated: false,
    isLoading: true,
    user: null,
    accessToken: null,
};
/**
 * Context for auth state
 */
const AuthContext = createContext(initialAuthState);
/**
 * AuthProvider component that wraps the app and provides auth state via context.
 *
 * CRITICAL FOR SESSION PERSISTENCE (AUTH-03):
 * On mount, calls fetchAuthSession() to restore session from stored Cognito tokens.
 * This enables user session to persist across browser refresh without re-login.
 *
 * Uses Hub.listen to react to auth events (signIn, signOut, redirect flows).
 */
export function AuthProvider({ children }) {
    const [authState, setAuthState] = useState(initialAuthState);
    /**
     * Check current authentication state
     * Called on mount and after auth events
     */
    const checkAuthState = useCallback(async () => {
        try {
            // Attempt to fetch session - this will refresh tokens if needed
            // This is critical for session persistence across browser refresh
            const session = await fetchAuthSession();
            if (session.tokens) {
                // Session exists with valid tokens - get user details
                const user = await getCurrentUser();
                const authUser = {
                    userId: user.userId,
                    // signInDetails may contain loginId (email) for username/password sign in
                    // For social sign in, we need to extract from user attributes
                    email: user.signInDetails?.loginId ?? user.userId,
                };
                const accessToken = session.tokens.accessToken?.toString() ?? null;
                setAuthState({
                    isAuthenticated: true,
                    isLoading: false,
                    user: authUser,
                    accessToken,
                });
            }
            else {
                // No valid session
                setAuthState({
                    isAuthenticated: false,
                    isLoading: false,
                    user: null,
                    accessToken: null,
                });
            }
        }
        catch {
            // Error fetching session - user not authenticated
            setAuthState({
                isAuthenticated: false,
                isLoading: false,
                user: null,
                accessToken: null,
            });
        }
    }, []);
    useEffect(() => {
        // Check auth state on mount - restores session from stored tokens
        checkAuthState();
        // Listen for auth events
        const hubListener = Hub.listen("auth", async ({ payload }) => {
            switch (payload.event) {
                case "signedIn":
                case "signInWithRedirect":
                    // User signed in - refresh auth state
                    await checkAuthState();
                    break;
                case "signedOut":
                    // User signed out - clear auth state
                    setAuthState({
                        isAuthenticated: false,
                        isLoading: false,
                        user: null,
                        accessToken: null,
                    });
                    break;
                case "signInWithRedirect_failure":
                    // Social sign in failed
                    setAuthState({
                        isAuthenticated: false,
                        isLoading: false,
                        user: null,
                        accessToken: null,
                    });
                    break;
                case "tokenRefresh":
                    // Tokens refreshed - update state with new tokens
                    await checkAuthState();
                    break;
                case "tokenRefresh_failure":
                    // Token refresh failed - user needs to re-authenticate
                    setAuthState({
                        isAuthenticated: false,
                        isLoading: false,
                        user: null,
                        accessToken: null,
                    });
                    break;
            }
        });
        // Cleanup listener on unmount
        return () => {
            hubListener();
        };
    }, [checkAuthState]);
    return _jsx(AuthContext.Provider, { value: authState, children: children });
}
/**
 * Hook to access auth state from context
 * Must be used within AuthProvider
 */
export function useAuthState() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuthState must be used within an AuthProvider");
    }
    return context;
}
//# sourceMappingURL=auth-provider.js.map